<script setup>
import AppLink from "@Components/AppLink.vue";
import AppLogo from "@Components/AppLogo.vue";
import { PrimaryButton } from "@Components/Button";
import Tooltip from "@Components/Tooltip.vue";
import ThemeSelector from "@Layouts/Partials/ThemeSelector.vue";
import { useStore as useUserStore } from "@Stores/user";
import { computed, getCurrentInstance } from "vue";
import { DiscordIcon, XTwitterIcon, YouTubeIcon } from "@Icons/index";
import { ArrowUpCircleIcon, HeadsetIcon, PlayIcon } from "lucide-vue-next";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import UsagesCard from "@Layouts/Partials/UsagesCard.vue";
import SubscriptionCard from "@Layouts/Partials/SubscriptionCard.vue";
import { ArrowLeftIcon } from "@heroicons/vue/16/solid";
import { useRouter } from "vue-router";
import { logout as authLogout } from "@/auth";
import { useI18n } from "vue-i18n";

defineProps({
    back: {
        type: Object,
        required: false,
    },
});

const { t: __ } = useI18n();
const router = useRouter();
const userStore = useUserStore();
const user = computed(() => userStore.user);

const logout = async () => {
    try {
        await authLogout();
        router.push({ name: "login" });
    } catch (e) {}
};
</script>
<template>
    <div class="h-full flex flex-col">
        <div class="flex flex-col flex-none px-6 py-3">
            <div class="flex justify-between items-center gap-x-3">
                <AppLink :to="{ name: 'dashboard' }">
                    <AppLogo />
                </AppLink>
                <!-- <ThemeSelector /> -->
            </div>
            <PrimaryButton
                :as="AppLink"
                v-if="getCurrentInstance().appContext.config.globalProperties.$app.version.outdated"
                :to="getCurrentInstance().appContext.config.globalProperties.$admin('plugins.php')"
                class="justify-center px-2 py-1 mt-3 text-xs"
                :z="1">
                {{ __("Nouvelle version disponible") }}
            </PrimaryButton>
        </div>

        <div v-if="back" class="flex-none px-6">
            <AppLink
                :to="back.to"
                :class="['text-gray-900 hover:text-gray-700 hover:underline',
                    'dark:text-gray-200 dark:hover:text-white',
                    'group flex gap-x-3 rounded-md py-2 text-xs items-center font-semibold',
                ]">
                <ArrowLeftIcon class="w-5 h-5 shrink-0" aria-hidden="true" />
                {{ back.label }}
            </AppLink>
        </div>

        <slot />

        <div v-if="user.subscription && user.subscription.product.key !== 'enterprise'" class="px-6 mt-3">
            <div class="flex flex-col gap-3 py-3 whitespace-nowrap rounded-xl border">
                <div class="px-3">
                    <div class="text-sm">
                        {{ __("Plan actuel :") }}
                        {{ user.subscription.product.name }}
                    </div>
                </div>
                <div class="px-3">
                    <AppLink
                        class="w-full relative inline-flex justify-center items-center gap-2.5 px-3 py-2 text-sm font-medium text-white bg-gray-800 rounded-md shadow-sm disabled:bg-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white hover:bg-gray-900"
                        to="https://studio.otomatic.ai/user/billing"
                        target="_blank">
                        <ArrowUpCircleIcon />
                        {{ __("Mise à niveau") }}
                    </AppLink>
                </div>
            </div>
        </div>

        <div class="flex flex-col flex-none mt-3">
            <div class="flex justify-center items-center px-6 space-x-3">
                <Tooltip :content="__('Retrouvez notre actualité')">
                    <AppLink to="https://x.com/otomaticAI" target="_blank">
                        <XTwitterIcon class="w-4 h-4 shrink-0 text-gray-800 dark:text-gray-200" />
                    </AppLink>
                </Tooltip>
                <Tooltip :content="__('Accédez aux vidéos')">
                    <AppLink to="https://www.youtube.com/@freudix" target="_blank">
                        <YouTubeIcon class="w-4 h-4 shrink-0 text-gray-800 dark:text-gray-200" />
                    </AppLink>
                </Tooltip>
                <Tooltip :content="__('Rejoignez la communauté')">
                    <AppLink to="https://discord.gg/wWwu8ReXAV" target="_blank">
                        <DiscordIcon class="w-4 h-4 shrink-0 text-gray-800 dark:text-gray-200" />
                    </AppLink>
                </Tooltip>
                <Tooltip content="hello@otomatic.ai">
                    <AppLink to="mailto:hello@otomatic.ai" target="_blank">
                        <HeadsetIcon class="w-4 h-4 shrink-0 text-gray-800 dark:text-gray-200" />
                    </AppLink>
                </Tooltip>
            </div>
        </div>

        <div class="flex flex-col flex-none mt-3 border-t border-gray-300 dark:border-gray-800">
            <Menu v-if="user" as="div" class="relative">
                <MenuButton as="div" class="flex justify-between items-center gap-x-4 px-6 py-4 cursor-pointer dark:hover:bg-gray-800 hover:bg-gray-50">
                    <div class="flex flex-1 gap-x-4">
                        <img class="w-10 h-10 bg-gray-800 rounded-md" :src="user.profile_photo_url" alt="" />
                        <div class="overflow-hidden flex flex-col">
                            <span class="text-sm text-gray-900 truncate dark:text-white">{{ user.name }}</span>
                            <span class="mt-1 text-xs text-gray-600 truncate dark:text-gray-400">{{ user.email }}</span>
                        </div>
                    </div>
                    <ChevronUpDownIcon class="w-5 h-5 flex-none text-gray-700 dark:text-gray-300" />
                </MenuButton>
                <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95">
                    <MenuItems class="-mt-2 -translate-y-full w-full absolute top-0 right-0 z-20 px-3 text-sm origin-bottom-right">
                        <div
                            class="bg-white rounded-md divide-y divide-gray-300 ring-1 ring-gray-300 shadow-lg dark:bg-gray-800 dark:border-gray-700 dark:divide-gray-700 dark:ring-gray-700 focus:outline-none">
                            <UsagesCard />
                            <!-- <SubscriptionCard /> -->
                            <div>
                                <MenuItem as="template" v-slot="{ active, selected }">
                                    <AppLink
                                        to="https://studio.otomatic.ai/dashboard"
                                        target="_blank"
                                        type="button"
                                        class="w-full relative block p-1 text-gray-900 cursor-pointer select-none dark:text-white">
                                        <div :class="[active ? 'bg-gray-50 dark:bg-gray-700' : '', 'rounded-sm py-2 px-3 flex items-center']">
                                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ __("Mon profil") }}</span>
                                        </div>
                                    </AppLink>
                                </MenuItem>
                                <MenuItem as="template" v-slot="{ active, selected }">
                                    <AppLink
                                        to="https://studio.otomatic.ai/user/billing"
                                        target="_blank"
                                        type="button"
                                        class="w-full relative block p-1 text-gray-900 cursor-pointer select-none dark:text-white">
                                        <div :class="[active ? 'bg-gray-50 dark:bg-gray-700' : '', 'rounded-sm py-2 px-3 flex items-center']">
                                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ __("Mon abonnement") }}</span>
                                        </div>
                                    </AppLink>
                                </MenuItem>
                                <MenuItem as="template" v-slot="{ active, selected }">
                                    <a
                                        href="mailto:hello@otomatic.ai"
                                        type="button"
                                        class="w-full relative block p-1 text-gray-900 cursor-pointer select-none dark:text-white">
                                        <div :class="[active ? 'bg-gray-50 dark:bg-gray-700' : '', 'rounded-sm py-2 px-3 flex items-center']">
                                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ __("Support") }}</span>
                                        </div>
                                    </a>
                                </MenuItem>
                                <MenuItem as="template" v-slot="{ active, selected }">
                                    <div @click="logout" class="w-full relative block p-1 text-gray-900 cursor-pointer select-none dark:text-white">
                                        <div :class="[active ? 'bg-gray-50 dark:bg-gray-700' : '', 'rounded-sm py-2 px-3 flex items-center']">
                                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ __("Se déconnecter") }}</span>
                                        </div>
                                    </div>
                                </MenuItem>
                            </div>
                        </div>
                    </MenuItems>
                </transition>
            </Menu>
        </div>
    </div>
</template>
